import { formatDollars } from '@fruitful/fruitils';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonVariant,
    Checkbox,
    LabelRegular,
    PlusIcon,
    redactPrivate,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import {
    BillDetailIcon,
    BillIconSize,
} from '../../bills/components/common/BillIcons';
import { useGetActiveBills } from '../../bills/hooks/useGetBills';
import useUpdateBill, {
    type UpdateBillBody,
} from '../../bills/hooks/useUpdateBill';
import { selectedBillAtom } from '../../bills/state/BillsAtoms';
import { type _Bill, BillActivationStatus } from '../../bills/types/Bill';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { track } from '../../logger/Logger';
import { useUpdateBankingAdoption } from '../hooks/useUpdateBankingAdoption';
import {
    type BankingAdoption,
    BankingAdoptionTaskStatus,
} from '../types/BankingAdoption';
import { BillsSwitchedSheet } from './BillsSwitchedSheet';

const SwitchableBill = ({
    bill,
    onPressRow,
    onToggleCheckbox,
}: {
    bill: Required<_Bill>;
    onPressRow: () => void;
    onToggleCheckbox: () => void;
}) => {
    const {
        aiBillId,
        amount,
        color,
        displayName,
        payee,
        icon,
        id,
        memberMarkedAsSwitched,
    } = bill;
    return (
        <XStack
            key={aiBillId}
            alignItems="center"
            padding={8}
            justifyContent="space-between"
        >
            <XStack
                gap={8}
                justifyContent="space-between"
                cursor="pointer"
                onPress={onPressRow}
            >
                <BillDetailIcon
                    icon={icon}
                    size={BillIconSize.SMALL}
                    color={color}
                />
                <YStack {...redactPrivate()}>
                    <BodyMediumRegular>
                        {displayName ?? payee}
                    </BodyMediumRegular>
                    <BodySmallRegular>
                        Est {formatDollars(amount, true)}
                    </BodySmallRegular>
                </YStack>
            </XStack>
            <Checkbox
                id={`${id}-checkbox`}
                checked={memberMarkedAsSwitched ?? false}
                onPress={onToggleCheckbox}
            />
        </XStack>
    );
};

export const SwitchableBills = () => {
    const user = useAuthedUser();
    const setSelectedBill = useSetAtom(selectedBillAtom);

    const queryClient = useQueryClient();

    const { activeBills: fetchedBills, isLoadingActiveBills } =
        useGetActiveBills();

    const { mutate: updateBill } = useUpdateBill();
    const { mutate: updateBankingAdoption } = useUpdateBankingAdoption();

    const [bills, setBills] = useState<Array<Required<_Bill>>>([]);
    const [showAllDoneSheet, setShowAllDoneSheet] = useState<boolean>(false);

    useEffect(() => {
        setBills(fetchedBills.length > 0 ? fetchedBills : []);
    }, [fetchedBills]);

    const update = useCallback(
        (bill: Required<_Bill>) => {
            const {
                amount,
                accountId,
                id,
                timingDescription,
                timingLabel,
                cadence,
                category,
                payee,
                displayName,
                lastPaidAt,
                memberMarkedAsSwitched,
            } = bill;
            const payload = !(memberMarkedAsSwitched ?? false);
            updateBill(
                {
                    urlParams: {
                        id,
                        membershipId: user?.membership?.id ?? '',
                    },
                    body: {
                        timingDescription,
                        lastPaidAt,
                        category,
                        timingLabel,
                        cadence,
                        payee,
                        amount,
                        accountId,
                        displayName,
                        memberMarkedAsSwitched: payload,
                    } satisfies UpdateBillBody,
                },
                {
                    onError: (_error) => {
                        void queryClient.invalidateQueries({
                            queryKey: [
                                `getBills/${user?.membership?.id ?? ''}/${BillActivationStatus.ACTIVE}`,
                            ],
                        });
                    },
                },
            );
            track(AnalyticsUserEvent.BANKING_ADOPTION_BILL_ITEM_TOGGLED, {
                billMarkedAsSwitched: payload,
                billId: id,
                billCategory: category,
            });
        },
        [user, updateBill, queryClient, bills],
    );

    const completeBillAdoption = useCallback(() => {
        updateBankingAdoption(
            {
                body: {
                    billSwitchStatus: BankingAdoptionTaskStatus.COMPLETE,
                },
            },
            {
                onSuccess: (newData: BankingAdoption) => {
                    if (newData.completedAt == null) {
                        setShowAllDoneSheet(true);
                    }
                    void queryClient.setQueryData(
                        ['getBankingAdoption'],
                        newData,
                    );
                },
            },
        );
    }, [queryClient]);

    if (isLoadingActiveBills) {
        return <Spinner />;
    }

    return (
        <YStack gap={16}>
            <YStack
                borderWidth={1}
                borderLeftWidth={1}
                borderColor="$gray600"
                borderRadius={12}
            >
                <XStack
                    backgroundColor="$gray200"
                    borderTopLeftRadius={12}
                    borderTopRightRadius={12}
                    gap={16}
                    padding={16}
                    alignItems="center"
                >
                    <BodyMediumRegular>Your bills</BodyMediumRegular>
                    <LabelRegular color="$gray1000">
                        {bills.length === 1
                            ? '1 bill'
                            : `${bills.length} bills`}
                    </LabelRegular>
                </XStack>
                {bills.map((bill) => {
                    return (
                        <SwitchableBill
                            key={bill.id}
                            bill={bill}
                            onPressRow={() => {
                                track(
                                    AnalyticsUserEvent.BANKING_ADOPTION_BILL_ROW_PRESSED,
                                );
                                setSelectedBill(bill);
                                router.navigate('/bills/bill-details-new');
                            }}
                            onToggleCheckbox={() => {
                                setBills((prevBills) => {
                                    const updatedBills = prevBills.map(
                                        (prevBill) => {
                                            if (prevBill.id === bill.id) {
                                                const payload = !(
                                                    prevBill.memberMarkedAsSwitched ??
                                                    false
                                                );
                                                update(prevBill);
                                                return {
                                                    ...prevBill,
                                                    memberMarkedAsSwitched:
                                                        payload,
                                                };
                                            } else {
                                                return prevBill;
                                            }
                                        },
                                    );
                                    if (
                                        updatedBills.every(
                                            (b) => b.memberMarkedAsSwitched,
                                        )
                                    ) {
                                        completeBillAdoption();
                                    }

                                    return updatedBills;
                                });
                            }}
                        />
                    );
                })}
            </YStack>
            <Button
                text="Add new bill"
                variant={ButtonVariant.SECONDARY}
                icon={PlusIcon}
                iconPosition="left"
                onPress={() => {
                    track(
                        AnalyticsUserEvent.BANKING_ADOPTION_ADD_NEW_BILL_PRESSED,
                    );
                    router.push('/bills/bill-add-new');
                }}
            />
            <BillsSwitchedSheet
                isOpen={showAllDoneSheet}
                onClose={() => {
                    setShowAllDoneSheet(false);
                }}
            />
        </YStack>
    );
};
